import { useEffect, useRef } from 'react';
import { GeocoderAutocomplete } from '@geoapify/geocoder-autocomplete';
import '@geoapify/geocoder-autocomplete/styles/minimal.css';
import { Input } from '@/components/ui/input';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
}

interface GeoapifyFeature {
  properties?: {
    formatted?: string;
    address_line1?: string;
    address_line2?: string;
  };
}

const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY?.trim();

export const AddressAutocomplete = ({
  value,
  onChange,
  placeholder,
  required = false,
}: AddressAutocompleteProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);

  onChangeRef.current = onChange;

  useEffect(() => {
    if (!apiKey || !containerRef.current) return;
    const container = containerRef.current;

    const autocomplete = new GeocoderAutocomplete(container, apiKey, {
      placeholder,
      lang: 'en',
      limit: 6,
      debounceDelay: 250,
      addDetails: true,
      allowNonVerifiedHouseNumber: true,
    });

    autocomplete.setValue(initialValueRef.current);
    const input = container.querySelector('input');
    if (input) input.required = required;

    const handleInput = (inputValue: string) => onChangeRef.current(inputValue);
    const handleClear = () => onChangeRef.current('');
    const handleSelect = (feature: GeoapifyFeature | null) => {
      if (!feature?.properties) return;
      const selectedAddress =
        feature.properties.formatted ||
        [feature.properties.address_line1, feature.properties.address_line2].filter(Boolean).join(', ');
      if (selectedAddress) onChangeRef.current(selectedAddress);
    };

    autocomplete.on('input', handleInput);
    autocomplete.on('clear', handleClear);
    autocomplete.on('select', handleSelect);

    return () => {
      autocomplete.off('input', handleInput);
      autocomplete.off('clear', handleClear);
      autocomplete.off('select', handleSelect);
      container.replaceChildren();
    };
  }, [placeholder, required]);

  if (!apiKey) {
    return (
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="street-address"
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="address-autocomplete"
      data-required={required || undefined}
    />
  );
};
